// ============================================
// Admin Event Detail
// ============================================

import React from 'react';
import { useParams, Routes, Route, Link, useLocation } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, collection, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Event, EventTherapist } from '../../types';

// Sub-pages
import EventLiveBoard from './event/LiveBoard';
import EventTherapists from './event/Therapists';
import TherapistPhotos from './event/TherapistPhotos';
import EventInvites from './event/Invites';
import EventSettings from './event/Settings';
import EventReports from './event/Reports';
import EventSettlements from './event/Settlements';
import SessionHealth from './event/SessionHealth';

export default function AdminEventDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const [event, setEvent] = React.useState<Event | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [pendingPhotos, setPendingPhotos] = React.useState(0);
  const [pendingSettlements, setPendingSettlements] = React.useState(0);

  React.useEffect(() => {
    if (!eventId) return;

    const unsubscribe = onSnapshot(doc(db, 'events', eventId), (docSnap) => {
      if (docSnap.exists()) {
        setEvent({ id: docSnap.id, ...docSnap.data() } as Event);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [eventId]);

  // Subscribe to pending photos count
  React.useEffect(() => {
    if (!eventId) return;

    const unsubscribe = onSnapshot(
      collection(db, 'events', eventId, 'therapists'),
      (snapshot) => {
        const count = snapshot.docs.filter((doc) => {
          const data = doc.data() as EventTherapist;
          return data.photoStatus === 'pending';
        }).length;
        setPendingPhotos(count);
      }
    );

    return unsubscribe;
  }, [eventId]);

  // Subscribe to pending settlements count
  React.useEffect(() => {
    if (!eventId) return;

    const settlementsQuery = query(
      collection(db, 'events', eventId, 'settlements'),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(settlementsQuery, (snapshot) => {
      setPendingSettlements(snapshot.docs.length);
    });

    return unsubscribe;
  }, [eventId]);

  const toggleEventStatus = async () => {
    if (!eventId || !event) return;

    const newStatus = event.status === 'live' ? 'draft' : 'live';
    await updateDoc(doc(db, 'events', eventId), { status: newStatus });
  };

  if (loading) {
    return <div className="loading">Loading event...</div>;
  }

  if (!event) {
    return <div className="error">Event not found</div>;
  }

  const basePath = `/admin/events/${eventId}`;
  const isActive = (path: string) => location.pathname === path || location.pathname === `${path}/`;

  return (
    <div className="admin-event-detail">
      <div className="event-header">
        <div className="event-header-info">
          <h1>{event.name}</h1>
          <p>{event.venueName}</p>
        </div>
        <div className="event-header-actions">
          <span className={`badge ${event.status}`}>{event.status.toUpperCase()}</span>
          <button
            onClick={toggleEventStatus}
            className={`btn btn-large ${event.status === 'live' ? 'btn-warning' : 'btn-success'}`}
          >
            {event.status === 'live' ? 'Stop Event' : 'Go Live'}
          </button>
        </div>
      </div>

      <nav className="nav-buttons">
        <Link to={basePath} className={`nav-btn ${isActive(basePath) ? 'active' : ''}`}>
          <span className="nav-icon">📊</span>
          <span className="nav-label">Live Board</span>
        </Link>
        <Link to={`${basePath}/therapists`} className={`nav-btn ${isActive(`${basePath}/therapists`) ? 'active' : ''}`}>
          <span className="nav-icon">👥</span>
          <span className="nav-label">Therapists</span>
        </Link>
        <Link to={`${basePath}/photos`} className={`nav-btn ${isActive(`${basePath}/photos`) ? 'active' : ''}`} style={{ position: 'relative' }}>
          <span className="nav-icon">📷</span>
          <span className="nav-label">Photos</span>
          {pendingPhotos > 0 && (
            <span style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}>
              {pendingPhotos}
            </span>
          )}
        </Link>
        <Link to={`${basePath}/invites`} className={`nav-btn ${isActive(`${basePath}/invites`) ? 'active' : ''}`}>
          <span className="nav-icon">🎟️</span>
          <span className="nav-label">Invites</span>
        </Link>
        <Link to={`${basePath}/reports`} className={`nav-btn ${isActive(`${basePath}/reports`) ? 'active' : ''}`}>
          <span className="nav-icon">📈</span>
          <span className="nav-label">Reports</span>
        </Link>
        <Link to={`${basePath}/settlements`} className={`nav-btn ${isActive(`${basePath}/settlements`) ? 'active' : ''}`} style={{ position: 'relative' }}>
          <span className="nav-icon">💰</span>
          <span className="nav-label">Settlements</span>
          {pendingSettlements > 0 && (
            <span style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              backgroundColor: '#f59e0b',
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 700,
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}>
              {pendingSettlements}
            </span>
          )}
        </Link>
        <Link to={`${basePath}/health`} className={`nav-btn ${isActive(`${basePath}/health`) ? 'active' : ''}`}>
          <span className="nav-icon">🩺</span>
          <span className="nav-label">Health</span>
        </Link>
        <Link to={`${basePath}/settings`} className={`nav-btn ${isActive(`${basePath}/settings`) ? 'active' : ''}`}>
          <span className="nav-icon">⚙️</span>
          <span className="nav-label">Settings</span>
        </Link>
      </nav>

      <div className="event-content">
        <Routes>
          <Route index element={<EventLiveBoard event={event} />} />
          <Route path="therapists" element={<EventTherapists event={event} />} />
          <Route path="photos" element={<TherapistPhotos event={event} />} />
          <Route path="invites" element={<EventInvites event={event} />} />
          <Route path="reports" element={<EventReports event={event} />} />
          <Route path="settlements" element={<EventSettlements event={event} />} />
          <Route path="health" element={<SessionHealth event={event} />} />
          <Route path="settings" element={<EventSettings event={event} />} />
        </Routes>
      </div>
    </div>
  );
}
